import React, { useState } from 'react';
import { Typography, IconButton, Tooltip, Box } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * A reusable component that displays text with a copy-to-clipboard icon.
 * 
 * @param {string} text - The text to be displayed and copied.
 * @param {string} label - Optional label to prefix the text.
 * @param {string} variant - MUI Typography variant (default: 'caption').
 * @param {string} color - MUI Typography color (default: 'text.secondary').
 * @param {object} sx - Optional sx props for the container Box.
 */
const CopyToClipboardText = ({ 
  text, 
  label, 
  variant = 'caption', 
  color = 'text.secondary',
  sx = {}
}) => {
  const [open, setOpen] = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation(); // Prevent trigger clicks on parent elements
    if (!text) return;
    
    navigator.clipboard.writeText(text).then(() => {
      setOpen(true);
      setTimeout(() => setOpen(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
    });
  };

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, ...sx }}>
      {label && (
        <Typography variant={variant} color={color} sx={{ fontWeight: 'bold' }}>
          {label}:
        </Typography>
      )}
      <Typography variant={variant} color={color} sx={{ fontFamily: 'monospace' }}>
        {text || 'N/A'}
      </Typography>
      {text && (
        <Tooltip 
          title="Copied!" 
          open={open} 
          disableFocusListener 
          disableHoverListener 
          disableTouchListener
          arrow
        >
          <IconButton 
            size="small" 
            onClick={handleCopy}
            sx={{ 
              padding: 0.2,
              '& .MuiSvgIcon-root': {
                fontSize: variant === 'caption' ? '0.9rem' : '1.1rem'
              }
            }}
          >
            <ContentCopyIcon color="action" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default CopyToClipboardText;
